import { useEffect, useState } from "react";
import axios from "axios";
import { authData } from "../../../admin/components/GetAuth";
import { hasFeature } from "../../../admin/components/Features";

// Shop Now (Shop add-on). The catalogue is public on the tenant site (v1/website/shop/*, by domain);
// the cart, checkout and orders belong to a signed-in customer (v1/customer/shop/*).

const API = process.env.REACT_APP_API_URL;
const customerHeaders = () => ({ Authorization: `Bearer ${authData()?.api_token}` });

export const isCustomer = () => {
  const auth = authData();
  return auth?.user_type === 5 && !!auth?.api_token;
};

// Tenant site: the website config says which add-ons the company has. Customer panel: the auth cookie.
export const shopEnabled = (websiteData) => (websiteData?.features ? websiteData.features.indexOf("shop") > -1 : false) || (isCustomer() && hasFeature("shop"));


export const fetchProducts = (domain, params) => (isCustomer()
  ? axios.get(API + "v1/customer/shop/products", { headers: customerHeaders(), params })
  : axios.get(API + "v1/website/shop/products", { params: { ...params, domain } }));

export const fetchProduct = (domain, id) => (isCustomer()
  ? axios.get(API + "v1/customer/shop/products/" + id, { headers: customerHeaders() })
  : axios.get(API + "v1/website/shop/products/" + id, { params: { domain } }));

// ---- Cart ----
// A signed-in customer's cart is saved on the server. A guest's cart lives in this browser
// ([{product_id, quantity}] per tenant domain) and is priced by v1/website/shop/cart-preview, so both
// look the same. Signing in moves the guest cart into the customer's cart (see getCart).

export const CART_EVENT = "efl-cart-updated";
export const CART_OPEN_EVENT = "efl-cart-open";

export const openCartDrawer = () => window.dispatchEvent(new Event(CART_OPEN_EVENT));

const announceCart = (cart) => {
  window.dispatchEvent(new CustomEvent(CART_EVENT, { detail: cart }));
  return cart;
};

const cartResult = (response) => {
  if (response.data.status !== "SUCCESS") {
    throw new Error(response.data.message || "Something went wrong.");
  }
  announceCart(response.data.data);
  return response.data;
};

const guestKey = () => "efl_cart_" + window.location.host;

// Storage can be blocked (private mode, site data off): treat that as an empty cart
const readGuestCart = () => {
  try {
    const items = JSON.parse(window.localStorage.getItem(guestKey()) || "[]");
    return Array.isArray(items) ? items.filter((i) => i && Number(i.product_id) > 0 && Number(i.quantity) > 0) : [];
  } catch (e) {
    return [];
  }
};

const writeGuestCart = (items) => {
  try {
    if (items.length) {
      window.localStorage.setItem(guestKey(), JSON.stringify(items));
    } else {
      window.localStorage.removeItem(guestKey());
    }
  } catch (e) { }
};

const previewGuestCart = (items = readGuestCart()) => axios.post(API + "v1/website/shop/cart-preview", { domain: window.location.host, items }).then((response) => {
  if (response.data.status === "SUCCESS") {
    // Forget items that left the shop
    const kept = (response.data.data?.items || []).map((l) => ({ product_id: l.product_id, quantity: l.quantity }));
    if (kept.length !== items.length) writeGuestCart(kept);
  }
  return cartResult(response);
});

// Signed-in customer: first move any guest cart from this browser into their saved cart
export const getCart = () => {
  if (!isCustomer()) return previewGuestCart();
  const guestItems = readGuestCart();
  if (guestItems.length) {
    return axios.post(API + "v1/customer/shop/cart/merge", { items: guestItems }, { headers: customerHeaders() }).then((response) => {
      writeGuestCart([]);
      return cartResult(response);
    });
  }
  return axios.get(API + "v1/customer/shop/cart", { headers: customerHeaders() }).then(cartResult);
};

// set=true replaces the line's quantity instead of adding to it
export const addToCart = (productId, quantity = 1, set = false) => {
  if (isCustomer()) {
    return axios.post(API + "v1/customer/shop/cart", { product_id: productId, quantity, set: set ? 1 : 0 }, { headers: customerHeaders() }).then(cartResult);
  }
  const before = readGuestCart();
  const line = before.find((i) => Number(i.product_id) === Number(productId));
  const qty = set ? quantity : (line ? Number(line.quantity) : 0) + quantity;
  const after = qty < 1
    ? before.filter((i) => Number(i.product_id) !== Number(productId))
    : line ? before.map((i) => (Number(i.product_id) === Number(productId) ? { ...i, quantity: qty } : i)) : before.concat([{ product_id: Number(productId), quantity: qty }]);
  return previewGuestCart(after).then((res) => {
    // Same stock rules as a signed-in cart: refuse more than is left
    const priced = res.data.items.find((l) => Number(l.product_id) === Number(productId));
    if (qty >= 1 && (!priced || priced.unavailable)) {
      previewGuestCart(before);
      const product = priced?.product;
      throw new Error(!priced ? "This item is no longer available." : product?.in_stock ? `Only ${product.max_qty} of ${product.title} left in stock.` : `${product?.title} is out of stock.`);
    }
    writeGuestCart(res.data.items.map((l) => ({ product_id: l.product_id, quantity: l.quantity })));
    return res;
  });
};

// Customer: the saved line id. Guest: the product id (that's the line id the preview gives back).
export const removeFromCart = (lineId) => {
  if (isCustomer()) {
    return axios.delete(API + "v1/customer/shop/cart/" + lineId, { headers: customerHeaders() }).then(cartResult);
  }
  const after = readGuestCart().filter((i) => Number(i.product_id) !== Number(lineId));
  writeGuestCart(after);
  return previewGuestCart(after);
};

export const checkout = (data) => axios.post(API + "v1/customer/shop/checkout", data, { headers: customerHeaders() }).then((response) => {
  if (response.data.status !== "SUCCESS") {
    throw new Error(response.data.message || "Your order could not be placed.");
  }
  announceCart({ items: [], subtotal: 0, count: 0 });
  return response.data;
});

export const fetchOrders = (params) => axios.get(API + "v1/customer/shop/orders", { headers: customerHeaders(), params });

export const fetchOrder = (id) => axios.get(API + "v1/customer/shop/orders/" + id, { headers: customerHeaders() });

// Item count for the header badge; follows every cart change made anywhere on the page
export const useCartCount = (enabled) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const onChange = (e) => setCount(e.detail?.count || 0);
    window.addEventListener(CART_EVENT, onChange);
    getCart().catch(() => { });
    return () => window.removeEventListener(CART_EVENT, onChange);
  }, [enabled]);

  return enabled ? count : 0;
};

export const formatPrice = (value) => "£" + Number(value || 0).toFixed(2);

export const CHECKOUT_PATH = "/customer/shop/checkout";

// Guests sign in (or sign up) first; their browser cart moves into the account on the way
export const checkoutPath = () => (isCustomer() ? CHECKOUT_PATH : "/sign-in?redirect=" + encodeURIComponent(CHECKOUT_PATH));

export const belowMinimum = (cart) => Number(cart?.min_order || 0) > 0 && Number(cart?.subtotal || 0) < Number(cart.min_order);

export const ORDER_PROGRESS_COLOR = {
  Placed: "#64748b",
  Confirmed: "#2563eb",
  "Out For Delivery": "#0d9488",
  Delivered: "#16a34a",
  Cancelled: "#dc2626",
};

export const PAYMENT_METHODS = [
  { value: "cod", label: "Pay on delivery", hint: "Pay the driver in cash or by card when your order arrives." },
  { value: "manual", label: "Bank transfer", hint: "Pay by bank transfer. We will share the payment details with you." },
];
